import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase/firebaseConfig";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp } from "firebase/firestore";
import Header from "./components/Header";
import { updateDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import SchedulingRequest from "./components/SchedulingRequest";
import { useBanner } from "./components/BannerContext";

function AthleteDashboard({ user }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [userId, setUserId] = useState(null);
  const [mbti, setMbti] = useState("");
  const [enneagram, setEnneagram] = useState("");
  const { t } = useTranslation();
  const [journalTitle, setJournalTitle] = useState("");
  const [journalContent, setJournalContent] = useState("");
  const [journalTags, setJournalTags] = useState("");
  const [journalEntries, setJournalEntries] = useState([]);
  const [activeTab, setActiveTab] = useState("sessions"); // or "journal"
  const [shareWithTherapist, setShareWithTherapist] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodNote, setMoodNote] = useState("");
  const [selectedJournalId, setSelectedJournalId] = useState(null);
  const [therapistId, setTherapistId] = useState(null);
  const isAthlete = user?.role === "athlete";
  const { showBanner } = useBanner();

  const moodOptions = [
    { emoji: "😞", label: "Frustrated" },
    { emoji: "😔", label: "Sad" },
    { emoji: "😐", label: "Neutral" },
    { emoji: "🙂", label: "Content" },
    { emoji: "😄", label: "Energized" },
    { emoji: "😤", label: "Anxious" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      setUserId(user.uid);

      // Fetch MBTI, Enneagram, Therapist ID
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setMbti(data.mbti || "");
        setEnneagram(data.enneagram || "");
        setTherapistId(data.therapistId || null);
      }

      // ✅ Fetch only past sessions from Firestore
      const pastQuery = query(
        collection(db, "sessions"),
        where("athleteId", "==", user.uid)
      );
      const pastSnapshot = await getDocs(pastQuery);
      const pastSessions = pastSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((s) => s.timestamp && new Date(s.timestamp) < new Date())
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSessions(pastSessions);

      // Fetch Journal Entries
      const journalSnapshot = await getDocs(
        collection(db, "users", user.uid, "journalEntries")
      );
      const journals = journalSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
      setJournalEntries(journals);

      await fetchJournalEntries(user.uid);
    };

    fetchData();
  }, []);

  const handleMoodSubmit = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !selectedMood) return;

      const moodRef = collection(db, "users", user.uid, "moodCheckins");

      await addDoc(moodRef, {
        moodLabel: selectedMood.label,
        moodEmoji: selectedMood.emoji,
        note: moodNote,
        createdAt: serverTimestamp(),
      });

      showBanner(t("mood_submitted_successfully"), "success");
      setSelectedMood(null);
      setMoodNote("");
    } catch (error) {
      console.error("❌ Error saving mood check-in:", error);
      showBanner(t("mood_submit_error"), "error");
    }
  };

  const fetchJournalEntries = async (uid) => {
    const journalSnapshot = await getDocs(
      collection(db, "users", uid, "journalEntries")
    );
    const journals = journalSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
    setJournalEntries(journals);
  };

  return (
    <>
      <Header user={user} />
      <div className="min-h-screen bg-white px-4 py-8">
        <div className="max-w-5xl mx-auto bg-[#f1efe7] rounded-xl shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{isAthlete ? "🏃 " + t("athlete_dashboard") : "💼 " + t("client_dashboard")}</h2>
          <div className="mb-6 bg-white p-4 rounded-md shadow-sm">
            <h3 className="text-lg font-semibold mb-2">{t("log_your_mood")}</h3>

            <div className="flex flex-wrap gap-3 mb-3">
              {moodOptions.map((mood) => (
                <button
                  key={mood.label}
                  onClick={() => setSelectedMood(mood)}
                  className={`flex items-center px-3 py-2 border rounded-full ${selectedMood?.label === mood.label ? "bg-[#b22222] text-white" : "bg-gray-50"
                    }`}
                >
                  <span className="text-xl mr-2">{mood.emoji}</span>
                  <span className="text-sm">{t(`mood_${mood.label.toLowerCase()}`)}</span>
                </button>
              ))}
            </div>

            <textarea
              placeholder={t("mood_optional_note")}
              value={moodNote}
              onChange={(e) => setMoodNote(e.target.value)}
              className="w-full p-2 mb-2 border rounded"
              rows={2}
            />

            <button
              className="bg-[#b22222] text-white px-4 py-2 rounded"
              onClick={handleMoodSubmit}
              disabled={!selectedMood}
            >
              {t("submit_mood")}
            </button>
          </div>

          {/* MBTI & Enneagram Overview */}
          <div className="mb-6 bg-white p-4 rounded-md shadow-sm">
            <p><strong>{t("mbti")}:</strong> {mbti || t("not_yet_entered")}</p>
            <p><strong>{t("enneagram")}:</strong> {enneagram || t("not_yet_entered")}</p>
          </div>

          <nav className="flex border-b border-gray-300 mb-6">
            {[
              { key: "sessions", label: t("your_sessions") },
              { key: "journal", label: t("your_journal") },
              { key: "hours", label: t("your_hours") },
            ].map((tab, index) => (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`cursor-pointer px-5 py-2 text-sm font-medium border border-b-0 rounded-t-md transition ${activeTab === tab.key
                  ? "bg-[#b22222] text-white border-[#b22222]"
                  : "bg-white text-gray-700 border-gray-300 hover:text-[#b22222]"
                  } ${index !== 0 ? "-ml-px" : ""}`}
              >
                {tab.label}
              </div>
            ))}
          </nav>


          {/* Journal Entry Section */}
          {activeTab === "journal" && (
            <div className="mb-8 bg-white p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold mb-2">{t("your_journal")}</h3>

              {/* Journal Form */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder={t("journal_title_placeholder")}
                  value={journalTitle}
                  onChange={(e) => setJournalTitle(e.target.value)}
                  className="w-full p-2 mb-2 border rounded"
                />
                <textarea
                  placeholder={t("journal_content_placeholder")}
                  value={journalContent}
                  onChange={(e) => setJournalContent(e.target.value)}
                  className="w-full p-2 mb-2 border rounded h-28"
                />
                <input
                  type="text"
                  placeholder={t("journal_tags_placeholder")}
                  value={journalTags}
                  onChange={(e) => setJournalTags(e.target.value)}
                  className="w-full p-2 mb-2 border rounded"
                />
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="shareWithTherapist"
                    checked={shareWithTherapist}
                    onChange={(e) => setShareWithTherapist(e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="shareWithTherapist" className="text-sm text-gray-700">
                    {isAthlete ? t("share_with_therapist") : t("share_with_therapist_c")}
                  </label>
                </div>

                <button
                  className="bg-[#b22222] text-white px-4 py-2 rounded"
                  onClick={async () => {
                    if (!journalContent.trim()) return showBanner("Please enter content.", "error");
                    try {
                      const journalRef = collection(db, "users", auth.currentUser.uid, "journalEntries");
                      await addDoc(journalRef, {
                        title: journalTitle,
                        content: journalContent,
                        tags: journalTags
                          .split(",")
                          .map((t) => t.trim())
                          .filter((t) => t),
                        createdAt: new Date(),
                        isRead: false,
                        shareWithTherapist: shareWithTherapist
                      });

                      setJournalTitle("");
                      setJournalContent("");
                      setJournalTags("");
                      setShareWithTherapist(false);

                      fetchJournalEntries(auth.currentUser.uid); // ✅ Pass the uid here
                    } catch (e) {
                      console.error("Error saving journal:", e);
                      showBanner("Error saving journal entry.", "error");
                    }
                  }}

                >
                  {t("save_journal_entry")}
                </button>
              </div>

              {/* Journal List */}
              <div className="space-y-3">
                {journalEntries.length === 0 ? (
                  <p className="text-gray-500">{t("no_journal_entries")}</p>
                ) : (
                  journalEntries.map((entry) => {
                    const isSelected = selectedJournalId === entry.id;

                    return (
                      <div
                        key={entry.id}
                        onClick={() =>
                          setSelectedJournalId(isSelected ? null : entry.id)
                        }
                        className={`cursor-pointer bg-[#f1efe7] p-4 rounded shadow transition ${isSelected ? "border-2 border-[#b22222]" : "border"
                          }`}
                      >
                        <p className="text-xs text-gray-500 mb-1">
                          {new Date((entry.createdAt?.seconds || 0) * 1000).toLocaleString()}
                        </p>

                        <div className="flex justify-between items-center">
                          <h4 className="text-md font-semibold">{entry.title}</h4>
                          <span className="text-sm text-gray-500">{isSelected ? "▲" : "▼"}</span>
                        </div>

                        <p className="text-sm whitespace-pre-line text-gray-800 mt-2">
                          {isSelected
                            ? entry.content
                            : entry.content.split("\n")[0].slice(0, 100) + (entry.content.length > 100 ? "..." : "")}
                        </p>

                        {isSelected && entry.tags?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-2">
                            {t("tags")}: {entry.tags.join(", ")}
                          </p>
                        )}
                      </div>
                    );
                  })

                )}
              </div>
            </div>
          )}

          {activeTab === "sessions" && (
            <>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("your_sessions")}</h3>

              {/* Schedule Next Session Widget */}
              <div className="mb-6 bg-white p-4 rounded-md shadow-sm">
                <h3 className="text-base font-semibold mb-3">{t("schedule_next_session")}</h3>
                {therapistId ? (
                  <SchedulingRequest therapistId={therapistId} />
                ) : (
                  <p className="text-sm text-gray-600">{t("no_therapist_assigned")}</p>
                )}
              </div>

              {/* Session List */}
              {sessions.length === 0 ? (
                <p className="text-gray-600">{t("no_sessions_yet")}</p>
              ) : (
                <ul className="space-y-3 mb-6">
                  {sessions
                    .filter(
                      (session) =>
                        session.timestamp && new Date(session.timestamp) < new Date()
                    )
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((session) => (
                      <li
                        key={session.id}
                        onClick={() =>
                          setSelectedSession((prev) =>
                            prev?.id === session.id ? null : session
                          )
                        }
                        className={`cursor-pointer p-4 rounded-md border ${selectedSession?.id === session.id
                          ? "border-[#b22222] bg-white"
                          : "border-gray-300 bg-white hover:border-[#b22222]"
                          }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">
                            {new Date(session.timestamp).toLocaleDateString()}
                          </span>
                          {session.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {session.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="text-xs bg-[#b22222] text-white px-2 py-1 rounded-full"
                                >
                                  {t(`tag_${tag.toLowerCase()}`) || tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {session.notes?.slice(0, 100)}...
                        </p>

                        {/* 👇 Show full details only if selected */}
                        {selectedSession?.id === session.id && (
                          <div className="mt-4 border-t pt-4 text-sm text-gray-800">
                            <p><strong>{t("date")}:</strong> {new Date(session.timestamp).toLocaleString()}</p>
                            <p><strong>{t("notes")}:</strong> {session.notes}</p>
                            {session.nextSteps && (
                              <p><strong>{t("next_steps")}:</strong> {session.nextSteps}</p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              {session.goalIdentification && (
                                <div>
                                  <strong>{t("goal_identification")}:</strong>
                                  <p>{session.goalIdentification}</p>
                                </div>
                              )}
                              {session.strongSelfActivation && (
                                <div>
                                  <strong>{t("strong_self_activation")}:</strong>
                                  <p>{session.strongSelfActivation}</p>
                                </div>
                              )}
                              {session.personalityTypeIdentification && (
                                <div>
                                  <strong>{t("personality_type_identification")}:</strong>
                                  <p>{session.personalityTypeIdentification}</p>
                                </div>
                              )}
                              {session.copingStrategyIdentification && (
                                <div>
                                  <strong>{t("coping_strategy_identification")}:</strong>
                                  <p>{session.copingStrategyIdentification}</p>
                                </div>
                              )}
                              {session.reprogrammingForSuccess && (
                                <div>
                                  <strong>{t("reprogramming_for_success")}:</strong>
                                  <p>{session.reprogrammingForSuccess}</p>
                                </div>
                              )}
                              {session.emotionalEmpowerment && (
                                <div>
                                  <strong>{t("emotional_empowerment")}:</strong>
                                  <p>{session.emotionalEmpowerment}</p>
                                </div>
                              )}
                            </div>

                            {/* Attached Files */}
                            {session.files?.length > 0 && (
                              <div className="mt-4">
                                <strong>{t("attached_files")}:</strong>
                                <ul className="list-disc list-inside">
                                  {session.files.map((url, index) => (
                                    <li key={index}>
                                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                        File {index + 1}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {session.athleteComment && (
                              <div className="mt-4 text-sm text-gray-700">
                                <strong>{t("your_previous_comment")}:</strong> {session.athleteComment}
                              </div>
                            )}

                            {/* Comment Input */}
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("add_comment")}
                              </label>
                              <textarea
                                rows="3"
                                value={session.tempComment || ""}
                                onChange={(e) => {
                                  setSessions((prev) =>
                                    prev.map((s) =>
                                      s.id === session.id ? { ...s, tempComment: e.target.value } : s
                                    )
                                  );
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                                placeholder={t("comment_placeholder")}
                              />
                              <button
                                onClick={async () => {
                                  try {
                                    await updateDoc(doc(db, "sessions", session.id), {
                                      athleteComment: session.tempComment,
                                    });
                                    showBanner("✅ Comment sent to your therapist.", "success");
                                    setSessions((prev) =>
                                      prev.map((s) =>
                                        s.id === session.id ? { ...s, athleteComment: session.tempComment, tempComment: "" } : s
                                      )
                                    );
                                  } catch (error) {
                                    console.error("❌ Failed to send comment:", error.message);
                                    showBanner("Error sending comment.", "error");
                                  }
                                }}
                                className="mt-2 bg-[#b22222] text-white px-4 py-2 rounded-md hover:bg-red-800 transition"
                              >
                                {t("send_to_therapist")}
                              </button>
                            </div>

                          </div>
                        )}
                      </li>
                    ))}
                </ul>

              )}
            </>
          )}

          {activeTab === "hours" && user && (() => {
            const allocated = user?.allocatedHours || 0;
            const used = user?.usedHours || 0;
            const remaining = (allocated - used).toFixed(2);

            return (
              <div className="bg-white p-4 rounded shadow-sm rtl:text-right ltr:text-left" dir="auto">
                <h3 className="text-lg font-semibold mb-4">{t("hours_summary")}</h3>

                <div className="mb-4 text-sm text-gray-700 space-y-1">
                  <div>⏱ <strong>{t("total_allocated_hours")}:</strong> {allocated.toFixed(2)} {t("hours_unit") || "ساعة"}</div>
                  <div>🔄 <strong>{t("total_used_hours")}:</strong> {used.toFixed(2)} {t("hours_unit") || "ساعة"}</div>
                  <div>💡 <strong>{t("remaining_hours")}:</strong> {remaining} {t("hours_unit") || "ساعة"}</div>
                </div>

                <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden mb-4">
                  <div
                    className="bg-[#b22222] h-full transition-all duration-300"
                    style={{ width: `${Math.min((used / allocated) * 100, 100)}%` }}
                  />
                </div>

                <h4 className="font-semibold text-gray-800 mb-2">{t("session_usage_list")}</h4>

                {sessions.length === 0 ? (
                  <p className="text-gray-500">{t("no_sessions")}</p>
                ) : (
                  <ul className="space-y-2">
                    {sessions.map((session) => (
                      <li
                        key={session.id}
                        className="border border-gray-300 p-3 rounded-md text-sm bg-gray-50"
                      >
                        🗓 {new Date(session.timestamp).toLocaleDateString()} — 🕒{" "}
                        {session.hoursUsed || 0.25} {t("hours_unit") || "ساعة"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

        </div>
      </div>
    </>
  );
}

export default AthleteDashboard;
